import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Shield, 
  User, 
  Eye, 
  Edit, 
  Trash2, 
  Plus,
  Search,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const ProductPermissionsSettings = () => {
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [productPermissions, setProductPermissions] = useState([]);
  const [showAddPermission, setShowAddPermission] = useState(false);
  const [newPermission, setNewPermission] = useState({
    user_id: '',
    permissions: []
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const permissionTypes = [
    { value: 'view', label: 'View', icon: Eye, description: 'Can view the product' },
    { value: 'edit', label: 'Edit', icon: Edit, description: 'Can edit product details' },
    { value: 'delete', label: 'Delete', icon: Trash2, description: 'Can delete the product' },
    { value: 'comment', label: 'Comment', icon: Edit, description: 'Can add notes and comments' }
  ];

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch products",
        variant: "destructive",
      });
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search`);
      setUsers(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    }
  };

  const fetchProductPermissions = async (productId) => {
    if (!productId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/product-permissions/${productId}`);
      setProductPermissions(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch product permissions",
        variant: "destructive",
      });
      setProductPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      fetchProductPermissions(selectedProduct);
    }
  }, [selectedProduct]);

  const handleAddPermission = async () => {
    if (!newPermission.user_id || newPermission.permissions.length === 0) {
      toast({
        title: "Error",
        description: "Please select a user and at least one permission",
        variant: "destructive",
      });
      return;
    }

    try {
      await axios.post(`${API}/admin/product-permissions`, {
        product_id: selectedProduct,
        user_id: newPermission.user_id,
        permissions: newPermission.permissions
      });

      setNewPermission({ user_id: '', permissions: [] });
      setShowAddPermission(false);
      fetchProductPermissions(selectedProduct);
      
      toast({
        title: "Success",
        description: "Permissions updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update permissions",
        variant: "destructive",
      });
    }
  };

  const handleRemovePermission = async (userId) => {
    if (!window.confirm('Are you sure you want to remove all permissions for this user?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/product-permissions/${selectedProduct}/${userId}`);
      fetchProductPermissions(selectedProduct);
      
      toast({
        title: "Success",
        description: "Permissions removed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove permissions",
        variant: "destructive",
      });
    }
  };

  const handlePermissionToggle = (permission) => {
    setNewPermission(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const getPermissionBadgeColor = (permission) => {
    const colors = {
      view: 'bg-blue-100 text-blue-800 border-blue-200',
      edit: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      delete: 'bg-red-100 text-red-800 border-red-200',
      comment: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[permission] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const filteredProducts = products.filter(product =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedProductData = products.find(p => p.id === selectedProduct);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-slate-900">Product Permissions</h3>
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Manage user permissions for products. Control who can view, edit, or delete each product.
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Product Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Select Product</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mb-2"
              />
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a product to manage permissions" />
                </SelectTrigger>
                <SelectContent>
                  {filteredProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {product.department}
                        </Badge>
                        <span>{product.title}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {selectedProduct && selectedProductData && (
          <>
            {/* Selected Product Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-medium text-slate-900">{selectedProductData.title}</h4>
                <Badge variant="outline">{selectedProductData.department}</Badge>
                <Badge className={`${selectedProductData.priority === 'high' ? 'bg-red-100 text-red-800' : 
                  selectedProductData.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-green-100 text-green-800'}`}>
                  {selectedProductData.priority}
                </Badge>
              </div>
              <p className="text-sm text-slate-600">{selectedProductData.description}</p>
            </div>

            {/* Current Permissions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-slate-900">Current Permissions</h4>
                <Button
                  onClick={() => setShowAddPermission(!showAddPermission)}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Permission
                </Button>
              </div>

              {/* Add Permission Form */}
              {showAddPermission && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Select User</Label>
                      <Select value={newPermission.user_id} onValueChange={(value) => setNewPermission(prev => ({...prev, user_id: value}))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose user" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => !productPermissions.some(p => p.user_id === u.id)).map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span>{user.full_name} (@{user.username})</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Permissions</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {permissionTypes.map((permType) => (
                        <div 
                          key={permType.value}
                          className="flex items-center space-x-2 p-2 border rounded hover:bg-slate-50 cursor-pointer"
                          onClick={() => handlePermissionToggle(permType.value)}
                        >
                          <Checkbox
                            checked={newPermission.permissions.includes(permType.value)}
                            onChange={() => handlePermissionToggle(permType.value)}
                          />
                          <div className="flex items-center gap-2">
                            <permType.icon className="w-4 h-4" />
                            <div>
                              <div className="text-sm font-medium">{permType.label}</div>
                              <div className="text-xs text-slate-500">{permType.description}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={handleAddPermission} size="sm">
                      Add Permission
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowAddPermission(false);
                        setNewPermission({ user_id: '', permissions: [] });
                      }}
                      variant="outline" 
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Permissions List */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="text-sm text-slate-500">Loading permissions...</div>
                </div>
              ) : productPermissions.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No custom permissions set</div>
                  <div className="text-xs text-slate-400 mt-1">Admin users and product creators have full access by default</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {productPermissions.map((permission) => (
                    <div key={permission.user_id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-slate-600" />
                            <span className="font-medium text-slate-900">
                              {permission.user_info?.full_name || 'Unknown User'}
                            </span>
                            <span className="text-sm text-slate-500">
                              @{permission.user_info?.username}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {permission.permissions.map((perm) => (
                              <Badge 
                                key={perm} 
                                className={`text-xs ${getPermissionBadgeColor(perm)}`}
                              >
                                {permissionTypes.find(pt => pt.value === perm)?.label}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button
                          onClick={() => handleRemovePermission(permission.user_id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p><strong>Permission System:</strong></p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li><strong>Admin users</strong> have full access to all products by default</li>
                    <li><strong>Product creators</strong> have full access to their own products</li>
                    <li><strong>Custom permissions</strong> can be set for specific users on specific products</li>
                    <li>Users without permissions won't see products in their dashboard or activity feed</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductPermissionsSettings;